import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import io from 'socket.io-client';

const EventChatModal = ({ event, onClose }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [showUserProfile, setShowUserProfile] = useState(null);
    const messagesEndRef = useRef(null);
    const socketRef = useRef(null);

    useEffect(() => {
        // Socket.IO verbinden
        socketRef.current = io(window.location.origin, {
            withCredentials: true,
            transports: ['websocket', 'polling']
        });

        // Chat-Nachrichten für dieses Event laden
        fetchMessages();

        // Socket Event Listener für neue Nachrichten
        socketRef.current.on('newChatMessage', (message) => {
            if (message.event_id === event.id) {
                setMessages(prev => [...prev, message]);
            }
        });

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
        };
    }, [event.id]);

    useEffect(() => {
        // Auto-scroll zu neuesten Nachrichten
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const fetchMessages = async () => {
        try {
            const response = await fetch(`/api/events/${event.id}/chat`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setMessages(data);
            }
        } catch (error) {
            console.error('Failed to load chat messages:', error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        try {
            const response = await fetch(`/api/events/${event.id}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ message: newMessage.trim() })
            });

            if (response.ok) {
                setNewMessage('');
            }
        } catch (error) {
            console.error('Failed to send message:', error);
        }
    };

    const handleUsernameClick = (msg) => {
        setShowUserProfile(msg);
    };

    const backdropStyle = {
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 1500,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '20px', boxSizing: 'border-box'
    };

    const modalStyle = {
        background: 'rgba(15, 21, 46, 0.95)',
        borderRadius: 'var(--border-radius-large)',
        border: '3px solid #000',
        boxShadow: '0 0 60px var(--color-button-blau)',
        maxWidth: '500px', width: '100%',
        maxHeight: '80vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden'
    };

    const headerStyle = {
        padding: '20px',
        borderBottom: '3px solid #000',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    };

    const messagesContainerStyle = {
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    };

    const messageStyle = (isOwn) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: isOwn ? 'flex-end' : 'flex-start',
        gap: '4px'
    });

    const messageBubbleStyle = (isOwn) => ({
        maxWidth: '75%',
        padding: '10px 14px',
        borderRadius: '12px',
        background: isOwn ? 'linear-gradient(90deg, #00c6ff, #0066cc)' : 'rgba(255, 255, 255, 0.1)',
        color: 'var(--color-text-weiss)',
        wordBreak: 'break-word'
    });

    const usernameStyle = {
        fontSize: '0.85rem',
        color: 'var(--color-text-hellblau)',
        cursor: 'pointer',
        textDecoration: 'underline',
        userSelect: 'none'
    };

    const inputContainerStyle = {
        padding: '15px',
        borderTop: '3px solid #000',
        display: 'flex',
        gap: '10px'
    };

    const inputStyle = {
        flex: 1,
        padding: '12px 16px',
        borderRadius: 'var(--border-radius-medium)',
        border: '3px solid #000',
        background: 'rgba(0, 0, 0, 0.3)',
        color: 'var(--color-text-weiss)',
        fontSize: '16px',
        outline: 'none'
    };

    const sendButtonStyle = {
        padding: '12px 24px',
        borderRadius: 'var(--border-radius-medium)',
        border: 'none',
        background: 'linear-gradient(90deg, #00c6ff, #0066cc)',
        color: 'var(--color-text-weiss)',
        fontSize: '16px',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 0 20px var(--color-button-blau)',
        userSelect: 'none'
    };

    const profileModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'rgba(15, 21, 46, 0.98)',
        borderRadius: 'var(--border-radius-large)',
        border: '3px solid #000',
        padding: '30px',
        boxShadow: '0 0 60px var(--color-button-blau)',
        textAlign: 'center',
        zIndex: 1600
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={backdropStyle}
        >
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                onClick={(e) => e.stopPropagation()}
                style={modalStyle}
            >
                <div style={headerStyle}>
                    <div>
                        <h2 style={{ margin: 0, color: 'var(--color-button-blau)', fontSize: '1.5rem' }}>
                            💬 {event.title}
                        </h2>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: 'var(--color-text-hellblau)' }}>
                            {t('chat')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-text-weiss)',
                            fontSize: '1.8rem',
                            cursor: 'pointer',
                            padding: '0',
                            lineHeight: '1'
                        }}
                    >
                        ✕
                    </button>
                </div>

                <div style={messagesContainerStyle}>
                    {messages.length === 0 ? (
                        <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '40px 20px' }}>
                            {t('no_messages_yet')}
                        </div>
                    ) : (
                        messages.map((msg, index) => {
                            const isOwn = msg.user_id === user?.id;
                            return (
                                <div key={index} style={messageStyle(isOwn)}>
                                    <span
                                        style={usernameStyle}
                                        onClick={() => handleUsernameClick(msg)}
                                    >
                                        {msg.nickname}
                                    </span>
                                    <div style={messageBubbleStyle(isOwn)}>
                                        {msg.message}
                                    </div>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            );
                        })
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSendMessage} style={inputContainerStyle}>
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={t('type_message_placeholder')}
                        style={inputStyle}
                        maxLength={500}
                        autoFocus
                    />
                    <button type="submit" disabled={!newMessage.trim()} style={sendButtonStyle}>
                        ➤
                    </button>
                </form>
            </motion.div>

            <AnimatePresence>
                {showUserProfile && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        onClick={() => setShowUserProfile(null)}
                        style={profileModalStyle}
                    >
                        <img
                            src={showUserProfile.selfie_path ? `/${showUserProfile.selfie_path}` : ''}
                            alt={showUserProfile.nickname}
                            style={{
                                width: '150px',
                                height: '150px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '3px solid var(--color-button-blau)',
                                marginBottom: '15px'
                            }}
                        />
                        <h3 style={{ margin: '0', color: 'var(--color-button-blau)' }}>
                            {showUserProfile.nickname}
                        </h3>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default React.memo(EventChatModal);
